// @flow

import React, { useEffect, useRef } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import * as selectors from "@modules/profile/selectors";
import actionCreators from "@modules/profile/actionCreator";
import type { MyAbsenceDraft } from "@modules/profile/reducer/my-absences/types";
import { Button, SchemaForm, type StepProps } from "@components";
import { uiSchemaFnc } from "./ui-schema-main-details";
import moment from "moment";

type PropsFromState = $Exact<{
  schema: Object | null,
  draft: MyAbsenceDraft | null,
  isValid: boolean,
}>;

type PropsFromDispatch = $Exact<{
  updateDraft: typeof actionCreators.SetMyAbsenceDraft,
  getMyAbsenceSchema: typeof actionCreators.GetMyAbsencesSchema,
}>;

type Props = $Exact<{
  ...StepProps,
  ...PropsFromLocalize,
  ...PropsFromState,
  ...PropsFromDispatch,
  clientId: number,
}>;

function MyAbsencesMainDetails(props: Props) {
  if (!props.editable) {
    return null;
  }

  const formRef = useRef(null);

  useEffect((): void => {
    props.getMyAbsenceSchema({
      clientId: props.clientId,
    });
  }, []);

  function nextStep(): void {
    if (formRef) {
      formRef.current.submit();
    }
  }

  function handleChange(formData): void {
    props.updateDraft({ ...props.draft, ...fixRTWDateIfAfterFitDate(formData) });
  }

  function handleSubmit(formData): void {
    // this is a method of Step component that will display the next step
    if (props.handleSubmit) {
      props.handleSubmit(formData, true);
    }
  }

  function fixRTWDateIfAfterFitDate(data) {
    let startsAt = moment(data.startsAt);
    let rtwAt = moment(data.rtwAt);

    if (moment(startsAt).isAfter(rtwAt)) {
      rtwAt = moment(startsAt).add(1, "minutes");
    }

    return {
      ...data,
      rtwAt: rtwAt.toISOString(),
    };
  }

  function draftData() {
    // set initial dates, startsAt = now
    const startsAt = moment().format();

    // fit and rtw dates > now + 1day
    const rtwAt = moment(startsAt)
      .add(1, "days")
      .format();

    return (
      props.draft || {
        startsAt,
        rtwAt,
      }
    );
  }

  return (
    <>
      <div className="w-100 d-flex justify-content-center">
        <div className="col-sm-8">
          <SchemaForm
            ref={formRef}
            className="w-100"
            formData={draftData()}
            schema={props.schema || {}}
            uiSchema={uiSchemaFnc({
              t: props.t,
              rtwAtMin: moment((props.draft || {}).startsAt).add(1, "minutes"),
            })}
            onChange={handleChange}
            onSubmit={handleSubmit}
            externalSubmit
          >
            <div className="d-flex justify-content-center" />
          </SchemaForm>
        </div>
      </div>
      <div className="w-100 d-flex justify-content-end my-3">
        <Button materialIcon="fast_forward" onClick={nextStep} disabled={!props.isValid}>
          {props.t("NEXT")}
        </Button>
      </div>
    </>
  );
}

const mapStateToProps = (state): PropsFromState => ({
  schema: selectors.myAbsenceSchema(state),
  draft: selectors.myAbsenceDraft(state),
  isValid: selectors.isMainDetailsValid(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  updateDraft: actionCreators.SetMyAbsenceDraft,
  getMyAbsenceSchema: actionCreators.GetMyAbsencesSchema,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(MyAbsencesMainDetails);
